import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { motion } from "motion/react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-20 bg-primary relative overflow-hidden" style={{ minHeight: "280px" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent" />
        <div className="relative w-full px-4 md:px-8 lg:px-12 py-16 md:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base">Last updated: January 1, 2024</p>
            <div className="w-16 h-1 bg-accent mx-auto mt-6 rounded-full" />
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
          <div className="space-y-10">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">1. Introduction</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Fiber One Pte. Ltd. ("we," "us," or "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and safeguard your information when you visit our website or use our services.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.05 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">2. Information We Collect</h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                We may collect the following types of information:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1.5 ml-1">
                <li><strong className="text-gray-800">Contact Information:</strong> Name, email address, phone number, company name, and job title when you submit inquiry forms or request quotes.</li>
                <li><strong className="text-gray-800">Technical Information:</strong> IP address, browser type, device information, and cookies to improve website performance and user experience.</li>
                <li><strong className="text-gray-800">Usage Data:</strong> Pages visited, time spent on pages, and interactions with content to analyze website traffic patterns.</li>
                <li><strong className="text-gray-800">Service Data:</strong> Project requirements, technical specifications, and support tickets submitted through our managed services portal.</li>
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">3. How We Use Your Information</h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                We use your information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1.5 ml-1">
                <li>To respond to your inquiries and provide quotations for our AV integration services.</li>
                <li>To process and manage service contracts and project implementations.</li>
                <li>To provide technical support and managed services for deployed systems.</li>
                <li>To send you newsletters, product updates, and marketing communications (with your consent).</li>
                <li>To improve our website, products, and services based on user behavior and feedback.</li>
                <li>To comply with legal obligations and enforce our terms of service.</li>
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">4. Information Sharing and Disclosure</h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                We do not sell or rent your personal information to third parties. We may share your information with:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1.5 ml-1">
                <li><strong className="text-gray-800">Service Providers:</strong> Trusted third-party vendors who assist in website hosting, analytics, and customer relationship management.</li>
                <li><strong className="text-gray-800">Project Partners:</strong> Subcontractors and technology manufacturers involved in specific AV integration projects with your prior approval.</li>
                <li><strong className="text-gray-800">Legal Requirements:</strong> When required by law, court order, or government regulation.</li>
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">5. Data Security</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                We implement industry-standard security measures including SSL encryption, access controls, and regular security audits to protect your data. However, no internet transmission is completely secure, and we cannot guarantee absolute security.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.25 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">6. Your Rights</h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                Depending on your jurisdiction, you may have the right to:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1.5 ml-1">
                <li>Access the personal data we hold about you.</li>
                <li>Request correction or deletion of your personal data.</li>
                <li>Object to or restrict certain types of data processing.</li>
                <li>Withdraw consent for marketing communications at any time.</li>
                <li>Request a copy of your data in a portable format.</li>
              </ul>
              <p className="text-sm text-gray-600 leading-relaxed mt-2">
                To exercise any of these rights, please contact us at <strong className="text-gray-800">privacy@fiberoneasia.com</strong>.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">7. Cookies</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Our website uses cookies to enhance user experience, analyze traffic, and personalize content. You can manage cookie preferences through your browser settings. For more details, please refer to our Cookie Policy.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.35 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">8. Children's Privacy</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                Our services are not directed at individuals under the age of 16. We do not knowingly collect personal information from children. If you believe we have inadvertently collected such data, please contact us immediately.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.4 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">9. Changes to This Policy</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. The updated policy will be posted on this page with a revised "Last updated" date.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.45 }}>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-3">10. Contact Us</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact our Data Protection Officer:
              </p>
              <div className="mt-3 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <p className="text-sm text-gray-700"><strong>Fiber One Pte. Ltd.</strong></p>
                <p className="text-sm text-gray-600">10 Anson Road, #22-07 International Plaza</p>
                <p className="text-sm text-gray-600">Singapore 079903</p>
                <p className="text-sm text-gray-600 mt-1">Email: privacy@fiberoneasia.com</p>
                <p className="text-sm text-gray-600">Phone: +65 6223 4567</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}